import {
  type Booking,
  type DisabledPrevButtonType,
  type Selected,
} from '../reducers/app/appTypes';
import { type SearchState } from '../reducers/search/searchTypes';
import { NO_STORE_ID } from '../services/appointment/model';
import type { OperationPage } from '../types/app';
import { isBrowser } from './browserHelper';

type Locale = {
  country: string;
  language: string;
};

export type BokaSession = {
  locale: Locale;
  booking: Booking | null;
  bookingCompleted: boolean;
  selected: Selected;
  search: SearchState;
  from: string;
  numberOfWeeksBack: number[];
  disabledPrevButton: DisabledPrevButtonType;
  operation: string;
  page: OperationPage | '';
};

type UpdateSessionItem = (
  key: keyof BokaSession,
  value:
    | string
    | boolean
    | Booking
    | Omit<Booking, 'orderNo'>
    | Partial<Selected>
    | Partial<SearchState>
    | number[],
) => void;

const BOKA_SESSION = 'boka_session';
const DEFAULT_SESSION: BokaSession = {
  locale: {
    country: CSS_CONFIG.country,
    language: CSS_CONFIG.language,
  },
  booking: null,
  bookingCompleted: false,
  selected: {
    serviceCode: '',
    serviceId: '',
    storeId: NO_STORE_ID,
    timeSlot: null,
    replacementService: {
      store: null,
      storeId: NO_STORE_ID,
      timeSlot: null,
    },
    language: null,
  },
  search: {
    postCode: '',
    city: '',
    address: '',
    state: '',
  },
  from: '',
  numberOfWeeksBack: [],
  disabledPrevButton: '',
  operation: '',
  page: '',
};

export const getSavedSession = (): BokaSession => {
  if (!isBrowser()) return DEFAULT_SESSION;
  const bokaSession = sessionStorage.getItem(BOKA_SESSION);
  return bokaSession
    ? { ...DEFAULT_SESSION, ...JSON.parse(bokaSession) }
    : DEFAULT_SESSION;
};

const saveSession = (sessionData: BokaSession) => {
  if (!isBrowser()) return;
  sessionStorage.setItem(BOKA_SESSION, JSON.stringify(sessionData));
};

let isCleared = false;

export const clearSessionData = (forceClear = true) => {
  if (!isBrowser() || (isCleared && !forceClear)) return;
  sessionStorage.removeItem(BOKA_SESSION);
  isCleared = true;
};

export const getInitialSessionOnClientEntry = (): BokaSession => {
  return getSavedSession();
};

export const saveUpdatedSessionItem: UpdateSessionItem = (key, value) => {
  if (!isBrowser()) return;
  const currentSession = getSavedSession();
  const isPartialObjectKey = key === 'selected' || key === 'search';
  if (isPartialObjectKey && typeof value === 'object') {
    const updatedSession = {
      ...currentSession,
      [key]: {
        ...currentSession[key],
        ...value,
      },
    };
    saveSession(updatedSession);
  } else {
    const updatedSession = {
      ...currentSession,
      [key]: value,
    };
    saveSession(updatedSession);
  }
};
